import { hasPermission, useAuth, type AppSession, type PermissionKey } from '@/features/auth'
// Import direct (pas le barrel `@/features/settings`), comme AppShell : évite le cycle passant par SettingsPage.
import { useModuleVisibilityList } from '@/features/settings/application/moduleVisibilityQueries'

interface HomeCandidate {
  path: string
  moduleKey: string
  permission: PermissionKey
}

/**
 * Accueils possibles, par ordre de priorité (décision du propriétaire du 23 oct. 2026, Vue d'ensemble V3) :
 * Vue d'ensemble, puis -2, puis -1, puis le Centre des opérations. Le 31 oct. 2026, les pages de Vue d'ensemble et
 * de -2 ont été inversées : l'ordre suit le nom et l'adresse, la vue du 12 oct. est donc l'accueil par défaut.
 */
export const homeCandidates: readonly HomeCandidate[] = [
  { path: '/vue-ensemble', moduleKey: 'vue_ensemble', permission: 'dashboard.read' },
  { path: '/vue-ensemble-2', moduleKey: 'vue_ensemble_2', permission: 'dashboard.read' },
  { path: '/vue-ensemble-1', moduleKey: 'vue_ensemble_1', permission: 'dashboard.read' },
  { path: '/centre-operations', moduleKey: 'centre_operations', permission: 'mission.read' },
]

/**
 * Premier accueil actif ET autorisé, `null` s'il n'y en a aucun. Ne renvoie jamais un module désactivé : la page
 * de repli ne peut donc pas rediriger vers elle-même (aucune boucle).
 */
export function resolveHomePath(
  session: AppSession | null | undefined,
  isModuleActive: (moduleKey: string) => boolean,
): string | null {
  if (!session) return null
  const home = homeCandidates.find(
    (candidate) => isModuleActive(candidate.moduleKey) && hasPermission(session, candidate.permission),
  )
  return home?.path ?? null
}

/**
 * Accueil de la personne connectée (logo, Accueil mobile). Même repli ouvert que la navigation : tant que la liste
 * des modules n'est pas connue, un module reste considéré actif.
 */
export function useHomePath(): string | null {
  const { session } = useAuth()
  const modules = useModuleVisibilityList().data?.modules
  return resolveHomePath(
    session,
    (moduleKey) => modules?.find((module) => module.moduleKey === moduleKey)?.isActive ?? true,
  )
}
